import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import * as requests from '../requests/index';
import { FolderPlanogram } from '../models/folder';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  public redirectUrl: string | null = null;
  public userId: BehaviorSubject<string | null> = new BehaviorSubject<
    string | null
  >(null);
  public authorized: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(private http: HttpClient, private router: Router) {}

  login(username: string, password: string): Observable<boolean> {
    const url = `${this.apiUrl}/auth/login`;
    return this.http.post<any>(
      url,
      new requests.LoginRequest(username, password)
    );
  }

  logout(): void {
    const url = `${this.apiUrl}/auth/logout`;
    this.http
      .get<any>(url, {
        observe: 'response',
      })
      .pipe(
        map((httpResponse) => {
          if (httpResponse.ok && httpResponse.body) {
            this.authorized.next(false);
            return true;
          } else {
            console.error('failed to log out');
            return false;
          }
        }),
        catchError((error) => {
          console.error('An error occurred while logging out:', error);
          return of(false);
        })
      )
      .subscribe((success) => {
        if (success) {
          this.router.navigate(['/login']);
        }
      });
  }

  checkPendingPlanogramRedirect(): Observable<FolderPlanogram | null> {
    const url = `${this.apiUrl}/auth/check-pending-planogram-redirect`;
    return this.http
      .get<FolderPlanogram | null>(url)
      .pipe(catchError(() => of(null)));
  }

isAuthorized(suppressRedirect = false): Observable<boolean> {
  const url = `${this.apiUrl}/auth/is-authorized`;
  return this.http.get<any>(url, { observe: 'response', withCredentials: true })
    .pipe(
      map((httpResponse) => {
        if (httpResponse.ok && httpResponse.body?.isAuthorized) {
          const currentUserId = httpResponse.body.userId;
          this.userId.next(currentUserId);
          this.authorized.next(true);
          return true;
        } else {
          this.authorized.next(false);
          this.userId.next(null);
          if (!suppressRedirect) this.router.navigate(['/login']);  // ← only redirect if needed
          return false;
        }
      }),
      catchError(() => {
        this.userId.next(null);
        this.authorized.next(false);
        if (!suppressRedirect) this.router.navigate(['/login']);   // ← only redirect if needed
        return of(false);
      })
    );
}

  resetPassword(email: string): Observable<any> {
    const url = `${this.apiUrl}/auth/reset-password`;
    return this.http.post(url, { email }).pipe(
      catchError((error) => {
        console.error('Reset Password Error', error);
        throw error;
      })
    );
  }

  confirmResetPassword(token: string, newPassword: string): Observable<any> {
    const url = `${this.apiUrl}/auth/confirm-reset-password`;
    return this.http.post(url, { token, newPassword }).pipe(
      catchError((error) => {
        console.error('Confirm Reset Password Error', error);
        throw error;
      })
    );
  }

  getUserId(): string | null {
    return this.userId.value;
  }
}
